import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  CandlestickChart,
  Refresh,
  Search,
  Timeline,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { colors, status } from '../../theme';

export default function HeaderSection({
  t,
  scanDate,
  mode,
  setMode,
  loading,
  fetchData,
  lookupTicker,
  setLookupTicker,
  handleLookupTicker,
  lookupLoading,
}) {
  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: { xs: 1.5, sm: 0 }, mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 2.5, flexShrink: 0,
            background: `linear-gradient(135deg, ${status.purple.main} 0%, ${status.purple.text} 100%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 4px 12px rgba(139,92,246,0.25)`,
          }}>
            <CandlestickChart sx={{ fontSize: 26, color: colors.background.paper }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: { xs: '1.1rem', sm: '1.35rem' }, fontWeight: 800, color: colors.gray[800], letterSpacing: -0.3 }}>
                {t('vpa.title')}
              </Typography>
              {scanDate && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: colors.gray[100], px: 1, py: 0.3, borderRadius: 1.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: status.purple.main, boxShadow: `0 0 6px ${status.purple.main}` }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: colors.gray[600] }}>{scanDate}</Typography>
                </Box>
              )}
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: colors.gray[400], fontWeight: 500 }}>
              {t('vpa.subtitle')}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', sm: 'auto' }, flexWrap: 'wrap' }} data-html2canvas-ignore="true">
          <ToggleButtonGroup
            value={mode}
            exclusive
            size="small"
            onChange={(_, value) => value && setMode(value)}
            sx={{
              bgcolor: colors.gray[100], borderRadius: '6px', border: 'none',
              '& .MuiToggleButton-root': {
                px: 2, py: 0.5, fontSize: 13, fontWeight: 600, textTransform: 'none',
                border: 'none', borderRadius: '6px !important', color: colors.gray[500],
                '&.Mui-selected': { bgcolor: colors.background.paper, color: colors.gray[800], boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
                '&.Mui-selected:hover': { bgcolor: colors.background.paper },
                '&:hover': { bgcolor: 'rgba(255,255,255,0.5)' },
              },
            }}
          >
            <ToggleButton value="default">{t('vpa.mode.default')}</ToggleButton>
            <ToggleButton value="conservative">{t('vpa.mode.conservative')}</ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="outlined"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Refresh sx={{ fontSize: 16 }} />}
            onClick={fetchData}
            disabled={loading}
            sx={{
              textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '6px',
              color: status.purple.text, borderColor: status.purple.border, px: 2,
              '&:hover': { bgcolor: status.purple.bg, borderColor: status.purple.main },
              '&:disabled': { bgcolor: colors.gray[100], color: colors.gray[400], borderColor: colors.gray[200] },
            }}
          >
            {loading ? t('vpa.scanning') : t('vpa.rescan')}
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          mb: 2.5,
          p: 1.5,
          borderRadius: 2,
          border: `1px solid ${status.purple.border}`,
          bgcolor: status.purple.bg,
          display: 'flex',
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: colors.gray[900], mb: 0.35 }}>
            {t('vpa.lookup.title')}
          </Typography>
          <Typography sx={{ fontSize: '0.72rem', color: colors.gray[500], lineHeight: 1.5 }}>
            {t('vpa.lookup.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', md: 'auto' }, flexShrink: 0 }}>
          <TextField
            size="small"
            placeholder={t('vpa.lookup.placeholder')}
            value={lookupTicker}
            onChange={(event) => setLookupTicker(event.target.value.toUpperCase())}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleLookupTicker();
              }
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16, color: alpha(status.purple.main, 0.75) }} /></InputAdornment>,
              sx: { fontSize: '0.8rem', bgcolor: colors.background.paper, borderRadius: 2 },
            }}
            sx={{ width: { xs: '100%', sm: 240 } }}
          />
          <Button
            variant="contained"
            onClick={handleLookupTicker}
            disabled={lookupLoading}
            startIcon={lookupLoading ? <CircularProgress size={16} color="inherit" /> : <Timeline sx={{ fontSize: 16 }} />}
            sx={{
              minWidth: 148,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '0.78rem',
              fontWeight: 700,
              bgcolor: status.purple.text,
              boxShadow: 'none',
              '&:hover': { bgcolor: colors.accumulationBreakout.dark, boxShadow: 'none' },
            }}
          >
            {lookupLoading ? t('vpa.lookup.checking') : t('vpa.lookup.button')}
          </Button>
        </Box>
      </Box>
    </>
  );
}
